import { User } from "@firebase/auth";
import { patientApi } from "@mgdx/api/lib/patientApi";
import { canUseAppBridge } from "@mgdx/shared/src/app-bridge/canUseAppBridge";
import { getAppNetUserIdFromApp, getAppUserIdFromApp, getIsInMccmApp } from "@mgdx/shared/src/app-bridge/getFromApp";
import { callShowBackButtonInApp, isCallableGetAppVersion } from "@mgdx/shared/src/app-bridge/publisher";
import { PatientProviderTypeEnum, PatientUpdateStatusEnum } from "@mgdx/shared/src/models/Patient";
import { PatientStatusEnum } from "@mgdx/shared/src/models/Patient";
import { setIsInMccmApp } from "@mgdx/shared/src/utils/isInMccmApp";
import { sleep } from "@mgdx/shared/src/utils/sleep";
import { useTalkStore } from "@mgdx/talk/providers/TalkStoreProvider";
import {
  apiErrorHandler,
  ApiNotFoundError,
  errorHandlerReport,
  firebaseErrorHandler,
  isFirebaseError,
} from "@mgdx-libs/error-handler";
import { safeWarning } from "@mgdx-libs/error-handler/lib/safeWarning";
import {
  clearAccessToken,
  FirebaseIndexedDBAbortError,
  FirebaseUserRoleNoneError,
  getFirebaseAuth,
  setAccessToken,
} from "@mgdx-libs/firebase";
import { navigate } from "@mgdx-libs/link";
import { logger } from "@mgdx-libs/logger";
import { useLocation } from "@reach/router";
import * as Sentry from "@sentry/react";
import { PropsWithChildren, useCallback, useState } from "react";

import { getInProcessOfLiffSignup } from "./hooks/useInProcessOfLiffSignup";
import { getInProcessOfOIDCSignup } from "./hooks/useInProcessOfOIDCSignup";
import { FirebaseAuthObserver, FirebaseAuthProvider } from "./providers/FirebaseAuthProvider";
import { clearServerState } from "./queryClient";
import { authStateStore, useIsInitialAuthCheckCompleted } from "./stores/authStateStore";
import { clearCurrentUser, setCurrentUser } from "./stores/currentUserStore";
import { clearPersistedState } from "./stores/persistStore";
import { ErrorForMccmApp } from "./ui/ErrorForMccmApp";
import { PreloaderForMccm } from "./ui/PreloaderForMccm";
import { FullscreenCoverPreloader } from "./ui/PreloaderForMccm/FullscreenCoverPreloader";
import { clearTokenAndState } from "./utils/user/clearTokenAndState";
import { getPatientFromFirebaseUserAndSetToStorage } from "./utils/user/getPatientFromFirebaseUserAndSetToStorage";
import { signoutAndClearToken } from "./utils/user/signoutAndClearToken";
import { updatePatientToEmailVerifiedAndSetToStore } from "./utils/user/updatePatientToEmailVerifiedAndSetToStore";
import { updatePatientWithMccmAppUserId } from "./utils/user/updatePatientWithMccmAppUserId";

const AuthWithEmailExisting = ({ children }: PropsWithChildren) => {
  const { disableTalk } = useTalkStore((state) => state.actions);
  const [isError, setIsError] = useState<boolean>(false);

  const firebaseAuthObserver = useCallback(
    async (firebaseUser: User | null) => {
      logger.debug("observer's auth type: email existing");
      // LIFF の起動・紐付け・新規登録の処理中は、各フックが認証状態を更新するため監視側では処理しない
      // (サインアウト時の後始末は処理中でも行う)
      // スキップしたイベントは onAuthStateChanged から再通知されないため、処理中フラグを立てるフックは
      // 認証状態を確立できないまま終わる場合にセッションと accessToken を破棄して未認証状態へ戻す責務を持つ
      if (firebaseUser && getInProcessOfLiffSignup()) return;

      if (firebaseUser) {
        logger.debug("firebase.User: %o", firebaseUser);
        const result = await firebaseUser.getIdTokenResult().catch(async (tokenError) => {
          if (tokenError instanceof FirebaseIndexedDBAbortError) {
            throw tokenError;
          }
          if (isFirebaseError(tokenError)) {
            try {
              firebaseErrorHandler(tokenError);
            } catch (e) {
              await errorHandlerReport(e as Error);
            }
          } else {
            await errorHandlerReport(tokenError);
          }
          return undefined;
        });

        if (result === undefined) {
          await navigate("/sign-out/", { replace: true });
          return;
        }

        if (result.claims.role !== "patient") {
          logger.debug("firebase.User: different role");
          return;
        }

        // 待機中にサインアウトや別ユーザーへの切り替えが起きた場合は、古いユーザーの情報を書き込まない
        if (getFirebaseAuth().currentUser?.uid !== firebaseUser.uid) {
          logger.debug("firebase.User: changed while resolving token");
          return;
        }

        logger.debug("getIdTokenResult: %o", result);
        setAccessToken(result.token);

        logger.debug("firebase.user.emailVerified: %s", firebaseUser.emailVerified);
        const patient = await patientApi.getCurrentPatient().catch(async (errorOrResponse) => {
          if (errorOrResponse instanceof Response) {
            // 待機中に別ユーザーへ切り替わった場合は、古いユーザーの取得失敗で新しいユーザーをサインアウトしない
            if (getFirebaseAuth().currentUser?.uid !== firebaseUser.uid) return undefined;

            await getFirebaseAuth().signOut();
            disableTalk();
            clearServerState();
            clearCurrentUser();
            clearAccessToken();
            clearPersistedState();
            try {
              throw await apiErrorHandler(errorOrResponse);
            } catch (e) {
              await errorHandlerReport(e);
            }
          } else {
            if (errorOrResponse instanceof DOMException) {
              try {
                throw await apiErrorHandler(errorOrResponse);
              } catch (e) {
                await errorHandlerReport(e as Error);
              }
            } else if (errorOrResponse instanceof Error) {
              await errorHandlerReport(errorOrResponse);
            }
          }

          return undefined;
        });

        if (!patient) return;

        // 患者取得中にサインアウトや別ユーザーへの切り替えが起きた場合は、古いユーザーの患者情報を書き込まない
        if (getFirebaseAuth().currentUser?.uid !== firebaseUser.uid) {
          logger.debug("firebase.User: changed while loading patient");
          return;
        }

        setCurrentUser(patient);

        const isEmailVerifiedStatus = patient.status === PatientStatusEnum.Verified;
        logger.debug("patient.status.emailVerified: %s", isEmailVerifiedStatus);

        if (
          firebaseUser.emailVerified !== isEmailVerifiedStatus &&
          patient.loginProviderType === PatientProviderTypeEnum.Email
        ) {
          if (firebaseUser.emailVerified) {
            // 別ユーザーへ切り替わった後に、古いユーザーのステータス更新を送らない
            if (getFirebaseAuth().currentUser?.uid !== firebaseUser.uid) return;

            logger.debug("update patient.status: %s", PatientUpdateStatusEnum.Verified);
            // エラーの報告は updatePatientToEmailVerifiedAndSetToStore 内で行う
            await updatePatientToEmailVerifiedAndSetToStore(firebaseUser).catch(() => {
              return;
            });
          }
        }
      } else {
        disableTalk();
        clearCurrentUser();
        clearAccessToken();
        clearPersistedState();
      }
    },
    [disableTalk]
  );

  const observer: FirebaseAuthObserver = useCallback(
    async (firebaseUser) => {
      await firebaseAuthObserver(firebaseUser).catch(async (error) => {
        if (error instanceof FirebaseIndexedDBAbortError) {
          // iOS WKWebView サスペンド時の IndexedDB AbortError は Sentry に記録し、エラー画面（再試行ボタン付き）を表示する
          safeWarning("observer: FirebaseIndexedDBAbortError, showing retry screen", {
            component: "AuthProvider",
          });
        } else if (isFirebaseError(error)) {
          await firebaseErrorHandler(error);
        } else {
          errorHandlerReport(error);
        }

        setIsError(true);
      });
    },
    [firebaseAuthObserver]
  );

  return <FirebaseAuthProvider observer={observer}>{isError ? <ErrorForMccmApp /> : children}</FirebaseAuthProvider>;
};

// memo: firebaseAuthObserverと同様の処理を行っている
const AuthWithEmail = ({ children }: PropsWithChildren) => {
  const { disableTalk } = useTalkStore((state) => state.actions);
  const [isError, setIsError] = useState<boolean>(false);

  const firebaseAuthWithEmail = useCallback(
    async (firebaseUser: User | null) => {
      logger.debug("observer's auth type: email");
      if (getInProcessOfOIDCSignup()) return;

      if (!firebaseUser) {
        disableTalk();
        clearCurrentUser();
        clearAccessToken();
        clearPersistedState();
        return;
      }

      const patient = await getPatientFromFirebaseUserAndSetToStorage(firebaseUser).catch(async (error) => {
        if (error instanceof FirebaseIndexedDBAbortError) {
          throw error;
        }
        logger.error(error);
        await signoutAndClearToken(disableTalk);
        navigate("/sign-in");
        return undefined;
      });

      if (!patient) return;

      logger.debug("firebase.user.emailVerified: %s", firebaseUser.emailVerified);

      const isPatientEmailVerifield = patient.status === PatientStatusEnum.Verified;
      logger.debug("patient.status.emailVerified: %s", isPatientEmailVerifield);

      if (patient.loginProviderType !== PatientProviderTypeEnum.Email) return;

      if (firebaseUser.emailVerified && !isPatientEmailVerifield) {
        logger.debug("update patient.status: %s", PatientUpdateStatusEnum.Verified);
        await updatePatientToEmailVerifiedAndSetToStore(firebaseUser).catch(() => {
          return;
        });
      }
    },
    [disableTalk]
  );

  const observer: FirebaseAuthObserver = useCallback(
    async (firebaseUser) => {
      await firebaseAuthWithEmail(firebaseUser).catch(async (error) => {
        if (error instanceof FirebaseIndexedDBAbortError) {
          // iOS WKWebView サスペンド時の IndexedDB AbortError は Sentry に記録し、エラー画面（再試行ボタン付き）を表示する
          safeWarning("observer: FirebaseIndexedDBAbortError, showing retry screen", {
            component: "AuthProvider",
          });
        } else if (isFirebaseError(error)) {
          await firebaseErrorHandler(error);
        } else {
          errorHandlerReport(error);
        }

        setIsError(true);
      });
      authStateStore.getState().actions.completeInitialAuthCheck();
    },
    [firebaseAuthWithEmail]
  );

  return <FirebaseAuthProvider observer={observer}>{isError ? <ErrorForMccmApp /> : children}</FirebaseAuthProvider>;
};

const authSkipPathList = ["/oidc/matsukiyococokara", "/sign-out", "/settings/leave"];

/**
 * app bridgeが利用可能になるまで待機する関数
 * sessionStorageへの設定が非同期で行われるため、リトライロジックを実装
 * @param maxRetries 最大試行回数 (デフォルト: 3回)
 * @param delayMs 試行間隔 (ミリ秒, デフォルト: 500ms)
 * @returns app bridgeが利用可能な場合true、タイムアウトした場合false
 */
const waitForAppBridge = async (maxRetries = 3, delayMs = 500): Promise<boolean> => {
  for (let i = 0; i < maxRetries; i++) {
    if (canUseAppBridge()) {
      logger.debug("App bridge is available (attempt: %d/%d)", i + 1, maxRetries);
      return true;
    }
    logger.debug("Waiting for app bridge... (attempt: %d/%d)", i + 1, maxRetries);
    await sleep(delayMs);
  }
  // 最終試行後に最後のチェックを実行
  if (canUseAppBridge()) {
    logger.debug("App bridge is available after final wait");
    return true;
  }
  logger.error("App bridge not available after %d attempts", maxRetries);
  return false;
};

const AuthInMccmApp = ({ children }: PropsWithChildren) => {
  const location = useLocation();
  const { disableTalk } = useTalkStore((state) => state.actions);
  const [isError, setIsError] = useState<boolean>(false);

  const isInSkipAuthPage = authSkipPathList.some((path) => location.pathname.startsWith(path));
  const shouldSkipAuth = isInSkipAuthPage || getInProcessOfOIDCSignup();

  const firebaseAuthWithMccmOidc = useCallback(
    async (firebaseUser: User | null) => {
      logger.debug("observer's auth type: mccm oidc");

      logger.debug("path", location.pathname);
      logger.debug("isInSkipAuthPage", isInSkipAuthPage);
      logger.debug("shouldSkipAuth", shouldSkipAuth);

      if (shouldSkipAuth) {
        authStateStore.getState().actions.completeInitialAuthCheck();
        return;
      }

      const redirectAndStartOIDCAuth = async () => {
        await navigate("/oidc/matsukiyococokara/redirect/?redirect_url=" + encodeURIComponent(window.location.href), {
          replace: true,
        });
      };

      if (!firebaseUser) {
        clearTokenAndState(disableTalk);
        clearPersistedState();
        await redirectAndStartOIDCAuth();

        return;
      }

      const patient = await getPatientFromFirebaseUserAndSetToStorage(firebaseUser).catch(async (error) => {
        if (error instanceof ApiNotFoundError || error instanceof FirebaseUserRoleNoneError) {
          await signoutAndClearToken(disableTalk);
          await redirectAndStartOIDCAuth();
          return undefined;
        }
        logger.error(error);
        throw error;
      });

      if (!patient) return;

      const yqbNetUserId = firebaseUser.uid.match(/oidc:matsukiyococokara-yqb:(\w+)/)?.[1];

      // app bridgeが利用可能になるまでリトライ
      const isAppBridgeAvailable = await waitForAppBridge();

      if (isAppBridgeAvailable) {
        // memo: アプリで調剤利用時に、アクセスして最初に1回だけ実行できればOKな処理
        // patient取得後でないと実行できないためここ(と新規利用開始、既存患者紐付け)で実行する。
        // ここは通常利用していれば調剤アクセス時１度しか呼ばれないはず
        getAppUserIdFromApp(async ({ appUserId }) => {
          await updatePatientWithMccmAppUserId(patient, appUserId);
        });

        getAppNetUserIdFromApp(async ({ netUserId }) => {
          if (netUserId !== yqbNetUserId) {
            await signoutAndClearToken(disableTalk);
            redirectAndStartOIDCAuth();

            if (
              process.env.BUILD_ENV === "dev" ||
              process.env.BUILD_ENV === "stg" ||
              process.env.BUILD_ENV === "mccmstg"
            ) {
              Sentry.captureMessage("debug: NetUserID不一致");
            }
          } else {
            callShowBackButtonInApp();
            authStateStore.getState().actions.completeInitialAuthCheck();

            if (
              process.env.BUILD_ENV === "dev" ||
              process.env.BUILD_ENV === "stg" ||
              process.env.BUILD_ENV === "mccmstg"
            ) {
              Sentry.captureMessage("debug: ログイン済み");
            }
          }
        });
      } else {
        // リトライ後もapp bridgeが利用できない場合はエラー
        const error = new Error("unexpected: can't use app bridge in daifuku app");
        logger.error(error.message);
        logger.debug("yqbNetUserId: %s", yqbNetUserId);

        throw error;
      }
    },
    [disableTalk, isInSkipAuthPage, location.pathname, shouldSkipAuth]
  );

  const observer: FirebaseAuthObserver = useCallback(
    async (firebaseUser) => {
      await firebaseAuthWithMccmOidc(firebaseUser).catch(async (error) => {
        if (error instanceof FirebaseIndexedDBAbortError) {
          // iOS WKWebView サスペンド時の IndexedDB AbortError は Sentry に記録し、エラー画面（再試行ボタン付き）を表示する
          safeWarning("observer: FirebaseIndexedDBAbortError, showing retry screen", {
            component: "AuthProvider",
          });
        } else if (isFirebaseError(error)) {
          await firebaseErrorHandler(error);
        } else {
          errorHandlerReport(error);
        }

        setIsError(true);
      });
    },
    [firebaseAuthWithMccmOidc]
  );

  return <FirebaseAuthProvider observer={observer}>{isError ? <ErrorForMccmApp /> : children}</FirebaseAuthProvider>;
};

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const isInitialAuthCheckCompleted = useIsInitialAuthCheckCompleted();
  const location = useLocation();
  const shouldWaitAuthCheckComplete =
    location.pathname.startsWith("/j") ||
    location.pathname.startsWith("/oidc/matsukiyococokara") ||
    new RegExp("^/pharmacy-counselings/\\d+/accept/").test(location.pathname);

  switch (process.env.MGDX_APP_NAME) {
    case "matsukiyococokara-yqb":
      if (isCallableGetAppVersion()) {
        getIsInMccmApp((isInMccmApp) => {
          setIsInMccmApp(isInMccmApp);
        });

        return (
          <AuthInMccmApp>
            {isInitialAuthCheckCompleted ? (
              children
            ) : shouldWaitAuthCheckComplete ? (
              <PreloaderForMccm />
            ) : (
              <FullscreenCoverPreloader>{children}</FullscreenCoverPreloader>
            )}
          </AuthInMccmApp>
        );
      } else {
        return (
          <AuthWithEmail>
            {isInitialAuthCheckCompleted ? (
              children
            ) : shouldWaitAuthCheckComplete ? (
              <PreloaderForMccm />
            ) : (
              <FullscreenCoverPreloader>{children}</FullscreenCoverPreloader>
            )}
          </AuthWithEmail>
        );
      }

    default:
      return <AuthWithEmailExisting>{children}</AuthWithEmailExisting>;
  }
};
